import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

type RevealState = 'idle' | 'hidden' | 'shown'

// Fondu et légère montée à la première apparition. Le rendu serveur reste visible :
// seuls les blocs encore hors écran après le montage sont masqués puis révélés.
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [state, setState] = useState<RevealState>('idle')

    useEffect(() => {
        const element = ref.current

        if (!element || typeof IntersectionObserver === 'undefined') return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        if (element.getBoundingClientRect().top < window.innerHeight * 0.9) return

        setState('hidden')

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry?.isIntersecting) return

            setState('shown')
            observer.disconnect()
        }, { rootMargin: '0px 0px -10% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            data-reveal={state}
            className={cn(className)}
            style={delay > 0 && state === 'shown' ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </div>
    )
}
